import { parseClinicianDateTimeText } from "./presentation.js";

export type ClinicianStructuredRecordFields = {
  clinician: string;
  date: string;
  dose: string;
  format: "newline" | "pipe";
  location: string;
  notes: string;
  passthrough: string;
  route: string;
  schedule: string;
  source: string;
  status: string;
  time: string;
};

type ClinicianStructuredRecordTextField = Exclude<
  keyof ClinicianStructuredRecordFields,
  "format"
>;

export type ClinicianStructuredRecordValidationErrors = Partial<
  Record<"date" | "time", string>
>;

function isValidCalendarDate(year: number, month: number, day: number): boolean {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    year < 1900 ||
    year > 2200 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return false;
  }

  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function isValidStructuredRecordDate(value: string): boolean {
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);

  if (isoMatch) {
    return isValidCalendarDate(
      Number.parseInt(isoMatch[1] ?? "0", 10),
      Number.parseInt(isoMatch[2] ?? "0", 10),
      Number.parseInt(isoMatch[3] ?? "0", 10),
    );
  }

  const numericMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/u.exec(value);

  if (numericMatch) {
    return isValidCalendarDate(
      Number.parseInt(numericMatch[3] ?? "0", 10),
      Number.parseInt(numericMatch[1] ?? "0", 10),
      Number.parseInt(numericMatch[2] ?? "0", 10),
    );
  }

  return parseClinicianDateTimeText(value) != null;
}

export function validateClinicianStructuredRecordFields(
  fields: ClinicianStructuredRecordFields,
  options: {
    previousFields?: ClinicianStructuredRecordFields | null;
  } = {},
): ClinicianStructuredRecordValidationErrors {
  const errors: ClinicianStructuredRecordValidationErrors = {};
  const date = fields.date.trim();
  const time = fields.time.trim();
  const previousDate = options.previousFields?.date.trim() ?? "";
  const previousTime = options.previousFields?.time.trim() ?? "";

  if (
    date.length > 0 &&
    date !== previousDate &&
    !isValidStructuredRecordDate(date)
  ) {
    errors.date = "Enter a real date, for example YYYY-MM-DD.";
  }

  if (
    time.length > 0 &&
    time !== previousTime &&
    !/^(?:(?:[01]\d|2[0-3]):[0-5]\d|(?:0?[1-9]|1[0-2]):[0-5]\d\s*(?:AM|PM))$/iu.test(
      time,
    )
  ) {
    errors.time = "Enter a valid time, for example 14:30 or 2:30 PM.";
  }

  return errors;
}

export function parseClinicianStructuredRecordFields(
  detailText: string,
): ClinicianStructuredRecordFields {
  const fields: ClinicianStructuredRecordFields = {
    clinician: "",
    date: "",
    dose: "",
    format: detailText.includes(" | ") ? "pipe" : "newline",
    location: "",
    notes: "",
    passthrough: "",
    route: "",
    schedule: "",
    source: "",
    status: "",
    time: "",
  };
  const fieldByLabel: Record<string, ClinicianStructuredRecordTextField> = {
    clinician: "clinician",
    date: "date",
    dose: "dose",
    location: "location",
    notes: "notes",
    route: "route",
    schedule: "schedule",
    source: "source",
    status: "status",
    time: "time",
  };
  const segments =
    fields.format === "pipe"
      ? detailText.split(/\s+\|\s+/u)
      : detailText.split(/\r?\n/u);
  const remainingSegments: string[] = [];
  let recognizedLineCount = 0;

  for (const segment of segments) {
    const match = /^([A-Za-z ]+):\s*(.*)$/u.exec(segment);
    const fieldName = match
      ? fieldByLabel[match[1].trim().toLowerCase()]
      : undefined;

    if (!match || fieldName == null) {
      remainingSegments.push(segment);
      continue;
    }

    recognizedLineCount += 1;
    // Projection text occasionally contains the same label more than once.
    // The final value is the effective one; retaining both would serialize an
    // embedded newline into a pipe segment and break the next parse.
    fields[fieldName] = match[2].trim();
  }

  if (recognizedLineCount === 0) {
    fields.notes = detailText;
  } else if (remainingSegments.some((segment) => segment.trim().length > 0)) {
    const remainingText = remainingSegments
      .join(fields.format === "pipe" ? " | " : "\n")
      .trim();

    if (fields.format === "pipe") {
      fields.passthrough = remainingText;
    } else {
      fields.notes = [fields.notes, remainingText]
        .filter((part) => part.length > 0)
        .join("\n");
    }
  }

  return fields;
}

export function serializeClinicianStructuredRecordFields(
  fields: ClinicianStructuredRecordFields,
): string {
  const labelledEntries = [
    ["Date", fields.date],
    ["Time", fields.time],
    ["Status", fields.status],
    ["Clinician", fields.clinician],
    ["Location", fields.location],
    ["Source", fields.source],
    ["Dose", fields.dose],
    ["Route", fields.route],
    ["Schedule", fields.schedule],
    ["Notes", fields.notes],
  ]
    .filter((entry) => entry[1].trim().length > 0)
    .map((entry) => `${entry[0]}: ${entry[1].trim()}`);
  const passthrough = fields.passthrough.trim();
  const entries =
    fields.format === "pipe"
      ? [passthrough, ...labelledEntries]
      : [...labelledEntries, passthrough];

  return entries
    .filter((entry) => entry.length > 0)
    .join(fields.format === "pipe" ? " | " : "\n");
}
